
import { mixins } from "vue-class-component";
import { Component, Prop, Watch } from "vue-property-decorator";
import { v4 as uuid } from "uuid";

import S3BucketMixin from "@/mixins/storage/S3BucketMixins";

import LIST_CONVERSATIONS from "@/apollo/conversations/ListConversations.gql";
import CHECK_CONVERSATIONS from "@/apollo/conversations/CheckConversations.gql";
import GET_CONVERSATION from "@/apollo/conversations/GetConversation.gql";
import CREATE_CONVERSATION from "@/apollo/conversations/CreateConversation.gql";
import CREATE_MESSAGE from "@/apollo/conversations/CreateMesssage.gql";

import NewConversationButtonWithModal from "@/components/inbox/NewConversationButtonWithModal.vue";
import PageHeadContainer from "@/components/page/PageHeadContainer.vue";
import LoadingOverlay from "@/components/common/LoadingOverlay.vue";

import InboxIcon from "@/components/icons/InboxIcon.vue";
import InboxWindow from "@/components/inbox/InboxWindowNew.vue";
import ScaffoldHeader from "@/components/scaffold/ScaffoldHeader.vue";
import UiButton from "@/components/button/ui-button.vue";
import InboxConversations from "@/components/inbox/InboxConversations.vue";
import EmptyListPlaceholder from "@/components/common/EmptyListPlaceholder.vue";

import { NOTIFICATION } from "@/entities/Events";
import Message from "@/data/notificationsMessages";
import {
  Company,
  Conversation,
  User,
  User_Conversation,
} from "@/generated/graphql";
import Roles from "@/entities/Roles";
import RouteNames from "@/entities/RouteNames";
import splitFileNameExt from "@/utils/splitFileNameExt";
import CustomScroll from "@/components/scroll/CustomScroll.vue";
import EmptyListImageInbox from "@/components/icons/emptyListImages/EmptyListImageInbox.vue";
import { getPredefindMessageKey } from "@/utils";
import getAuthData from "@/utils/getAuthData";

interface NewMessage {
  attachment: File;
  content: string;
  id: string;
}

interface IConversationParticipants {
  userId: string;
  targetId: string;
}

// @ts-ignore
@Component({
  components: {
    EmptyListImageInbox,
    CustomScroll,
    NewConversationButtonWithModal,
    PageHeadContainer,
    LoadingOverlay,
    InboxWindow,
    InboxIcon,
    ScaffoldHeader,
    UiButton,
    InboxConversations,
    EmptyListPlaceholder,
  },
  apollo: {
    conversations: {
      query: LIST_CONVERSATIONS,
      variables() {
        return {
          currentUserId: this.currentUser.id,
          where: {
            users: {
              _and: [{ user_id: { _eq: this.currentUser.id } }],
            },
            _and: { is_custom_request_conversation: { _eq: false } },
          },
        };
      },
      update(data) {
        const conversations = data.conversation;
        function latestMessageTimestamp(conversation) {
          const messages = conversation.messages;

          if (!messages || messages.length === 0) {
            return 0;
          }

          const latestTimestamp = Math.max(
            ...messages.map((message) => Date.parse(message.created_at))
          );

          return latestTimestamp;
        }
        conversations.sort((a, b) => {
          const latestTimestampA = latestMessageTimestamp(a);
          const latestTimestampB = latestMessageTimestamp(b);
          return latestTimestampB - latestTimestampA;
        });
        return conversations;
      },
    },
    $subscribe: {
      conversationList: {
        query: CHECK_CONVERSATIONS,
        variables() {
          return {
            currentUserId: this.currentUser.id,
            where: {
              user_id: { _eq: this.currentUser.id },
              _and: {
                conversation: {
                  is_custom_request_conversation: { _eq: false },
                },
              },
            },
          };
        },
        result(response: any) {
          //@ts-ignore
          this.conversationsUpdated = response.data.user_conversation;
        },
      },
      conversation: {
        query: GET_CONVERSATION,
        variables() {
          //@ts-ignore
          return {
            id: this.selectedConversationId,
            currentUserId: this.currentUser.id,
          };
        },
        //@ts-ignore
        skip() {
          //@ts-ignore
          return !this.selectedConversationId;
        },
        result(response: any) {
          //@ts-ignore
          this.conversation = response.data.conversation_by_pk;
        },
      },
    },
  },
})
export default class Inbox extends mixins(S3BucketMixin) {
  @Prop({ type: Object, required: true }) readonly currentUser!: User;
  @Prop({ type: Object, default: undefined }) readonly company!: Company;
  @Prop({ type: String, default: undefined }) readonly type!: string;
  @Prop({ type: String, default: undefined }) readonly conversationId?: string;
  @Prop({ type: String, default: undefined }) readonly message?: string;

  selectedConversationId: string | null = null;
  conversations: Conversation[] = [];
  conversation?: Conversation | null = null;
  inviteDialog: boolean = false;
  recipientUserId: string = "";
  loading: boolean = false;
  predefinedMessage = "";
  conversationsUpdated: Conversation[] = [];
  isMobile: boolean = window.innerWidth <= 768;
  locallyCreated: boolean = false;
  loadingConversation: boolean = false;

  noConversationsPlaceholder: string =
    this.isMobile ? "Loading conversation" : "You currently do not have any active conversations";
  // emptyListSubtitle: string = "Start conversations now"

  uploadingAttachmentId: string = "";

  beforeDestroy() {
    window.removeEventListener("resize", this.handleResize);
  }

  get empty(): boolean {
    return this.conversations.length === 0;
  }

  get isDataLoaded(): boolean {
    return this.conversations.length > 0 || !this.apolloLoading;
  }
  selectConversation(conversationId) {
    this.loadingConversation = true;
    this.selectedConversationId = conversationId;
  }

  clearSelectedConversation() {
    this.conversation = null;
    this.selectedConversationId = null;
  }
  handleResize() {
    this.isMobile = window.innerWidth <= 768;
  }

  get apolloLoading(): boolean {
    return this.$apollo.queries.conversations.loading;
  }

  get isContractor(): boolean {
    return this.currentUser.role === Roles.CONTRACTOR;
  }

  async mounted() {
    window.addEventListener("resize", this.handleResize);
    let newMessage = this.message || "";
    if (this.$route.query.recipientUserId) {
      this.recipientUserId = this.$route.query.recipientUserId as string;
      const sessionMessage = window.sessionStorage.getItem(
        getPredefindMessageKey(this.recipientUserId)
      );
      if (sessionMessage) {
        window.sessionStorage.removeItem(
          getPredefindMessageKey(this.recipientUserId)
        );
        newMessage = sessionMessage;
      }
    }
    this.predefinedMessage = newMessage;
    this.clearQueryParams("recipientUserId");
  }

  latestMessageTimestamp(conversation) {
    const messages = conversation.messages;

    // If there are no messages, return a very low timestamp (e.g., 0) for sorting
    if (!messages || messages.length === 0) {
      return 0; // or use a default date
    }

    // Find the latest message by sorting messages based on timestamp and selecting the first one
    const latestMessage = messages.reduce((latest, message) => {
      if (!latest || message.created_at > latest.created_at) {
        return message;
      }
      return latest;
    }, null);
  }

  @Watch("conversationId")
  onConversationIdChanged(id: string) {
    this.selectedConversationId = id;
  }

  @Watch("selectedConversationId")
  onConversationSelected(id: string) {
    if (id) {
      this.$apollo.subscriptions.conversation.skip = false;
    }
  }

  @Watch("conversations", { deep: true })
  async onConversationsLoaded(
    newVal: Conversation[],
    oldValue: Conversation[]
  ) {
    if (
      newVal.length > oldValue.length &&
      oldValue.length > 0 &&
      !this.locallyCreated
    ) {
      this.$eventBus.emit(
        NOTIFICATION,
        Message.infoMessage("You have a new conversation")
      );
    }
    this.loadingConversation = true;

    if (this.conversationId) {
      this.loadingConversation = false;
      this.selectedConversationId = this.conversationId;
      return;
    }

    if (this.recipientUserId) {
      this.loading = true;
      const input = {
        userId: this.currentUser.id,
        targetId: this.recipientUserId,
      };

      try {
        const conversation = await this._findConversation(input);
        await this.handleReuseCreateConversation(conversation, input);
        this.recipientUserId = "";
        this.loading = false;
      } catch (e) {
        this.loading = false;
        this.$eventBus.emit(NOTIFICATION, Message.errorMessage(e.toString()));
      }
    }
  }

  async sendMessage({ attachment, content, id }: NewMessage) {
    if (!attachment) {
      return await this._createMessage(content);
    }

    const uid = uuid();
    const { ext, name } = splitFileNameExt(attachment.name);
    const filename = `${name}-${uid}.${ext}`;
    this.uploadingAttachmentId = id;

    await this.setFile(filename, attachment);
    await this._createMessage(content, filename);
  }

  async handleCreateConversation(targetId: string): Promise<void> {
    if (!targetId) {
      return;
    }
    const input = {
      userId: this.currentUser.id,
      targetId,
    };
    const conversation = await this._findConversation(input);
    try {
      await this.handleReuseCreateConversation(conversation, input);
    } catch (e) {
      if (e.name !== "NavigationDuplicated") {
        this.$eventBus.emit(
          NOTIFICATION,
          Message.errorMessage(
            "Something went wrong. Couldn't create a conversation"
          )
        );
      }
    }
  }

  async handleReuseCreateConversation(
    conversation: Conversation | null,
    input: { userId: string; targetId: string; }
  ): Promise<void> {
    if (conversation?.id) {
      this.selectedConversationId = conversation.id;
    } else {
      this.selectedConversationId = await this._createConversation(input);
    }

    await this.$router.replace({
      name: RouteNames.INBOX_CONVERSATION,
      params: { conversationId: this.selectedConversationId! },
    });
    await this.$apollo.queries.conversations.refetch();
  }

  async _findConversation(input: IConversationParticipants) {
    const conversations = this.conversations.filter((conversation) => {
      return (
        conversation.users.length <= 2 &&
        conversation.users.find(
          (u: User_Conversation) => u.user.id === input.userId
        ) &&
        conversation.users.find(
          (u: User_Conversation) => u.user.id === input.targetId && !u.deleted
        )
      );
    });
    return conversations.length > 0 ? conversations[0] : null;
  }

  async _createConversation(input: IConversationParticipants): Promise<string> {
    const object = {
      users: {
        data: [{ user_id: input.userId }, { user_id: input.targetId }],
      },
    };
    this.locallyCreated = true;
    const created = await this.$apollo.mutate({
      mutation: CREATE_CONVERSATION,
      variables: { object },
    });
    return created.data.insert_conversation_one.id;
  }

  async _createMessage(message: string, key?: string) {
    if (!this.conversation) return;
    const recipientMessage = this.conversation.users
      .filter((user) => user.user.id !== this.currentUser.id)
      .map((user) => ({
        recipient_id: user.user.id,
        conversation_id: this.conversation!.id,
      }));

    const object = {
      attachment: key || null,
      content: message || null,
      conversation_id: this.conversation.id,
      sender_id: this.currentUser.id,
      recipient_message: { data: recipientMessage },
    };

    const resp = await this.$apollo.mutate({
      mutation: CREATE_MESSAGE,
      variables: { object },
    });
    this.triggerSendChatEmail(resp?.data?.insert_message_one);
    return resp;
  }

  async triggerSendChatEmail(message) {
    const uri = `${process.env.VUE_APP_LAMBDA}/send-chat-email`;
    const { headers } = await getAuthData();
    const { data }: any = await this.axios.post(uri, {
      data: {
        message,
      },
    }, { headers }
    );
  }

  clearQueryParams(queryName: string): void {
    this.$router.replace({ query: { [queryName]: undefined } });
  }

  async handleConversationDeleted(id: string) {
    this.selectedConversationId = null;
    await this.$apollo.queries.conversations.refetch();
    this.conversations = this.conversations.filter(
      (conversation) => id !== conversation.id
    );
  }
}
